import React, { useState } from "react";

import { StorefrontData } from "data/storefront";

import { CREATORS_PAGE_SHARE_MODAL_SHARED } from "~/analytics/Amplitude/amplitudeConstants";
import { addUTMPropertiesToEvent } from "~/analytics/Amplitude/amplitudeUtils";
import { useAnalytics } from "~/analytics/useAnalytics";
import { EmailIcon } from "~/components/common/Icons/EmailIcon";
import { FacebookIcon } from "~/components/common/Icons/FacebookIcon";
import { LinkedinIcon } from "~/components/common/Icons/LinkedinIcon";
import { MessengerIcon } from "~/components/common/Icons/MessengerIcon";
import { TwitterIcon } from "~/components/common/Icons/TwitterIcon";
import { InputFieldWrap } from "~/components/common/InputFieldWrap";
import { useIsMobileScreen } from "~/hooks/ui/useIsMobileScreen";

import * as baseStyles from "../FormLayout.css";
import * as styles from "./ShareModal.css";

interface Props {
  storefrontData: StorefrontData;
}
export const ShareModal = ({ storefrontData }: Props): React.ReactElement => {
  const isMobileScreen = useIsMobileScreen();
  const { trackAmplitudeEvent } = useAnalytics();

  const [rightActionTxt, setRightActionTxt] = useState("Copy");
  const shareUrl = `${window.location.origin}/${storefrontData.slug}`;
  const bodyText = `Check out ${storefrontData.creatorName}'s Studio`;

  const trackEvent = (type: string) => {
    trackAmplitudeEvent(CREATORS_PAGE_SHARE_MODAL_SHARED, {
      ...addUTMPropertiesToEvent(),
      type: type,
      creatorName: storefrontData.creatorName,
    });
  };

  const shareOnSocialMedia = (url: string, type: string) => {
    trackEvent(type);
    openSharePopup(url);
  };

  const handleRightAction = () => {
    if (rightActionTxt === "Copy") {
      trackEvent("copied");
      setRightActionTxt("Copied!");
      navigator.clipboard.writeText(shareUrl);
      setTimeout(() => {
        setRightActionTxt("Copy");
      }, 3000);
    }
  };

  const openSharePopup = (url: string) => {
    var width = 600;
    var height = 400;
    var leftPosition, topPosition;
    leftPosition = window.screen.width / 2 - (width / 2 + 10);
    topPosition = window.screen.height / 2 - (height / 2 + 50);
    var windowFeatures =
      "status=no,height=" +
      height +
      ",width=" +
      width +
      ",resizable=yes,left=" +
      leftPosition +
      ",top=" +
      topPosition +
      ",screenX=" +
      leftPosition +
      ",screenY=" +
      topPosition +
      ",toolbar=no,menubar=no,scrollbars=no,location=no,directories=no";
    window.open(url, "sharer", windowFeatures);
  };

  const socialMedias = [
    {
      icon: <FacebookIcon />,
      type: "facebook",
      url: `http://www.facebook.com/sharer.php?u=${encodeURIComponent(
        shareUrl,
      )}&t=${encodeURIComponent(bodyText)}`,
    },
    {
      icon: <MessengerIcon />,
      type: "messenger",
      url: `fb-messenger://share?link=${encodeURIComponent(shareUrl)}`,
    },
    {
      icon: <EmailIcon />,
      type: "email",
      url: `mailto:?subject=${encodeURIComponent(bodyText)}&body=${encodeURIComponent(shareUrl)}`,
    },
    {
      icon: <LinkedinIcon />,
      type: "linkedin",
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      icon: <TwitterIcon />,
      type: "twitter",
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        bodyText,
      )}&url=${encodeURIComponent(shareUrl)}`,
    },
  ];

  return (
    <div className={baseStyles.container}>
      <div className={baseStyles.topSection}>
        <div className={styles.titleRow}>
          <h2
            className={styles.title}
          >{`Share ${storefrontData.creatorName.trim()}'s Studio with friends`}</h2>
        </div>
        <div className={styles.ShareBoxRow}>
          <div className={styles.socialWrap}>
            <div className={styles.socials}>
              {socialMedias.map((socialMedia) => (
                <>
                  {socialMedia.type === "messenger" && !isMobileScreen ? (
                    <></>
                  ) : (
                    <div
                      key={socialMedia.type}
                      className={styles.social}
                      onClick={() =>
                        shareOnSocialMedia(socialMedia.url + shareUrl, socialMedia.type)
                      }
                    >
                      {socialMedia.icon}
                    </div>
                  )}
                </>
              ))}
            </div>
          </div>
          <InputFieldWrap
            type="text"
            readOnly={true}
            value={shareUrl}
            rightAction={{
              text: rightActionTxt,
              enabled: true,
              callback: () => {
                handleRightAction();
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};
